/**
 * shareUtils — compose a watermarked share image and trigger native Web Share / download.
 * Used by Studio (avatar / camera capture) to make it dead-simple for users to
 * post their MUZI BATTLE creations on Instagram / TikTok stories.
 */

const STORY_W = 1080;
const STORY_H = 1920;

/**
 * Load an image source (dataURL, blob URL, or http URL) into an HTMLImageElement.
 */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

/**
 * Compose a 9:16 share-ready image:
 *  - magenta→purple→cyan gradient background (Y2K vibe)
 *  - centered avatar/photo (cover-fit, rounded glow)
 *  - MUZI BATTLE chrome-pink logo on top
 *  - tagline + URL at bottom
 *
 * Returns a Blob (image/png).
 */
export async function composeShareableImage(srcDataUrl, opts = {}) {
  const { tagline = 'Chante. Joue. Deviens viral.', username = '' } = opts;
  const canvas = document.createElement('canvas');
  canvas.width = STORY_W;
  canvas.height = STORY_H;
  const ctx = canvas.getContext('2d');

  // ----- Background gradient
  const grd = ctx.createLinearGradient(0, 0, STORY_W, STORY_H);
  grd.addColorStop(0, '#1a0033');
  grd.addColorStop(0.45, '#5b0e6b');
  grd.addColorStop(0.85, '#e91e63');
  grd.addColorStop(1, '#00e5ff');
  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, STORY_W, STORY_H);

  // ----- subtle noise overlay (grain)
  ctx.globalAlpha = 0.06;
  for (let i = 0; i < 1800; i++) {
    ctx.fillStyle = 'white';
    ctx.fillRect(Math.random() * STORY_W, Math.random() * STORY_H, 1.2, 1.2);
  }
  ctx.globalAlpha = 1;

  // ----- Avatar / photo
  const img = await loadImage(srcDataUrl);
  // Square crop area, centered
  const SIZE = 880;
  const x = (STORY_W - SIZE) / 2;
  const y = 380;

  // Outer chrome ring (gradient stroke)
  const ring = ctx.createLinearGradient(x, y, x + SIZE, y + SIZE);
  ring.addColorStop(0, '#ff00ea');
  ring.addColorStop(0.5, '#ffffff');
  ring.addColorStop(1, '#00e5ff');

  ctx.save();
  ctx.shadowColor = 'rgba(255, 0, 234, 0.6)';
  ctx.shadowBlur = 60;
  ctx.fillStyle = ring;
  roundRect(ctx, x - 12, y - 12, SIZE + 24, SIZE + 24, 48);
  ctx.fill();
  ctx.restore();

  // Avatar image, cover-fit into SIZE×SIZE rounded box
  ctx.save();
  roundRect(ctx, x, y, SIZE, SIZE, 40);
  ctx.clip();
  // Cover-fit
  const ar = img.width / img.height;
  let dw = SIZE, dh = SIZE, sx = 0, sy = 0, sw = img.width, sh = img.height;
  if (ar > 1) {
    sw = img.height; sx = (img.width - sw) / 2;
  } else if (ar < 1) {
    sh = img.width; sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, dw, dh);
  ctx.restore();

  // ----- Headline "MUZI BATTLE"
  ctx.save();
  ctx.textAlign = 'center';
  ctx.fillStyle = '#ffffff';
  ctx.font = '900 130px "Helvetica Neue", Arial, sans-serif';
  ctx.shadowColor = '#ff1493';
  ctx.shadowBlur = 30;
  ctx.fillText('MUZI BATTLE', STORY_W / 2, 220);
  // Chrome-pink shimmer
  const head = ctx.createLinearGradient(0, 130, 0, 240);
  head.addColorStop(0, '#ffffff');
  head.addColorStop(0.5, '#ff66cc');
  head.addColorStop(1, '#ff007a');
  ctx.fillStyle = head;
  ctx.shadowBlur = 0;
  ctx.fillText('MUZI BATTLE', STORY_W / 2, 220);
  ctx.restore();

  // ----- Tagline
  ctx.save();
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255,255,255,0.92)';
  ctx.font = '600 42px "Helvetica Neue", Arial, sans-serif';
  ctx.fillText(tagline, STORY_W / 2, 295);
  ctx.restore();

  // ----- Username badge if provided
  if (username) {
    ctx.save();
    const text = `@${username}`;
    ctx.font = '700 36px "Helvetica Neue", Arial, sans-serif';
    const w = ctx.measureText(text).width + 56;
    const bx = (STORY_W - w) / 2;
    const by = y + SIZE + 40;
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    roundRect(ctx, bx, by, w, 70, 35);
    ctx.fill();
    ctx.fillStyle = '#ff66cc';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, STORY_W / 2, by + 36);
    ctx.restore();
  }

  // ----- Footer URL
  ctx.save();
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255,255,255,0.92)';
  ctx.font = '700 44px "Helvetica Neue", Arial, sans-serif';
  ctx.shadowColor = '#00e5ff';
  ctx.shadowBlur = 14;
  ctx.fillText('muzibattle.com', STORY_W / 2, STORY_H - 140);
  ctx.shadowBlur = 0;
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = '500 28px "Helvetica Neue", Arial, sans-serif';
  ctx.fillText('Bêta fermée — Code requis', STORY_W / 2, STORY_H - 90);
  ctx.restore();

  return await new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.95));
}

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/**
 * Try to use Web Share API (native share sheet on mobile incl. iOS Safari).
 * Falls back to downloading the file.
 * Returns 'shared' | 'downloaded' | 'cancelled'.
 */
export async function shareImage(blob, { filename = 'muzibattle.png', text = 'MUZI BATTLE — Chante. Joue. Deviens viral.' } = {}) {
  if (!blob) return 'cancelled';
  const file = new File([blob], filename, { type: blob.type || 'image/png' });

  // Modern share with files
  if (navigator.canShare?.({ files: [file] }) && navigator.share) {
    try {
      await navigator.share({ files: [file], title: 'MUZI BATTLE', text });
      return 'shared';
    } catch (err) {
      if (err?.name === 'AbortError') return 'cancelled';
      console.warn('[shareImage] navigator.share failed, falling back to download', err);
    }
  }

  // Fallback download
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
  return 'downloaded';
}

/**
 * Capture the current video frame as data URL (mirrored to match what the user sees).
 */
export function captureFrameFromVideo(video) {
  if (!video || !video.videoWidth) return null;
  const c = document.createElement('canvas');
  const size = Math.min(video.videoWidth, video.videoHeight, 1024);
  c.width = size;
  c.height = size;
  const cx = c.getContext('2d');
  const sx = (video.videoWidth - size) / 2;
  const sy = (video.videoHeight - size) / 2;
  cx.save();
  cx.scale(-1, 1);
  cx.translate(-size, 0);
  cx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
  cx.restore();
  return c.toDataURL('image/jpeg', 0.9);
}
